import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { serviceTokensApi, ServiceToken, CreateServiceTokenDto } from "../utils/api";
import { SecretKeyModal } from "../components/SecretKeyModal";
import { AdminLayout } from "../components/AdminLayout";
import { formatRelativeTime as formatRelativeTimeUtil } from "../utils/formatting";
import { filterServiceTokens } from "../utils/serviceTokens";

export const ServiceTokens = () => {
  const { token } = useAuth();
  const { t } = useTranslation();

  // Data state
  const [serviceTokens, setServiceTokens] = useState<ServiceToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // UI state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [showSecretKeyModal, setShowSecretKeyModal] = useState(false);
  const [secretKey, setSecretKey] = useState("");
  const [secretKeyName, setSecretKeyName] = useState("");

  // Filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  // Selected item
  const [selectedToken, setSelectedToken] = useState<ServiceToken | null>(null);

  // Form state
  const [formData, setFormData] = useState<CreateServiceTokenDto>({
    service_name: "",
    description: "",
  });
  const [formError, setFormError] = useState("");
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    if (!token) return;
    try {
      setLoading(true);
      const tokensData = await serviceTokensApi.getAll(token);
      setServiceTokens(tokensData);
      setError("");
    } catch (err: any) {
      setError(t("serviceTokens.failedToLoad"));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Filter service tokens
  const filteredTokens = useMemo(() => {
    return filterServiceTokens(serviceTokens, searchTerm, statusFilter);
  }, [serviceTokens, searchTerm, statusFilter]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    setFormLoading(true);
    setFormError("");

    try {
      const result = await serviceTokensApi.create(token, formData);
      setShowCreateModal(false);
      setFormData({
        service_name: "",
        description: "",
      });
      setSecretKey(result.token);
      setSecretKeyName(result.service_name);
      setShowSecretKeyModal(true);
      await fetchData();
    } catch (err: any) {
      setFormError(err.message || t("serviceTokens.failedToCreate"));
    } finally {
      setFormLoading(false);
    }
  };

  const openDeleteModal = (serviceToken: ServiceToken) => {
    setSelectedToken(serviceToken);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!token || !selectedToken) return;

    setFormLoading(true);
    setFormError("");

    try {
      await serviceTokensApi.delete(token, selectedToken.id);
      setShowDeleteModal(false);
      setSelectedToken(null);
      await fetchData();
    } catch (err: any) {
      setFormError(err.message || t("serviceTokens.failedToDelete"));
    } finally {
      setFormLoading(false);
    }
  };

  const openRegenerateModal = (serviceToken: ServiceToken) => {
    setSelectedToken(serviceToken);
    setFormError("");
    setShowRegenerateModal(true);
  };

  const handleRegenerate = async () => {
    if (!token || !selectedToken) return;

    setFormLoading(true);
    setFormError("");

    try {
      const result = await serviceTokensApi.regenerate(token, selectedToken.id);
      setShowRegenerateModal(false);
      setSelectedToken(null);
      setSecretKey(result.token);
      setSecretKeyName(result.service_name);
      setShowSecretKeyModal(true);
      await fetchData();
    } catch (err: any) {
      setFormError(err.message || t("serviceTokens.failedToRegenerate"));
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggleStatus = async (serviceToken: ServiceToken) => {
    if (!token) return;

    try {
      if (serviceToken.is_active) {
        await serviceTokensApi.deactivate(token, serviceToken.id);
      } else {
        await serviceTokensApi.activate(token, serviceToken.id);
      }
      await fetchData();
    } catch (err: any) {
      console.error("Failed to toggle status:", err);
    }
  };

  const formatRelativeTime = (dateString: string | null) => {
    return formatRelativeTimeUtil(dateString, t);
  };

  return (
    <AdminLayout title={t("serviceTokens.title")} subtitle={t("serviceTokens.subtitle")}>
      {loading ? (
        <div className="text-center py-16">
          <div className="text-gray-600">{t("common.loading")}</div>
        </div>
      ) : (
        <div className="">
          {error && <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>}

          {/* Filters and Search */}
          <div className="mb-6 bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder={t("serviceTokens.searchPlaceholder")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--gold)] focus:border-[var(--gold)]"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--gold)] focus:border-[var(--gold)]"
              >
                <option value="all">{t("serviceTokens.allStatus")}</option>
                <option value="active">{t("common.active")}</option>
                <option value="inactive">{t("common.inactive")}</option>
              </select>
            </div>
          </div>

          {/* Header with Create button */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{t("serviceTokens.allServiceTokens", { count: filteredTokens.length })}</h3>
            </div>
            <button
              onClick={() => {
                setFormData({
                  service_name: "",
                  description: "",
                });
                setFormError("");
                setShowCreateModal(true);
              }}
              className="px-4 py-2 bg-[var(--navy)] text-white rounded-lg hover:bg-[var(--navy-light)] transition-colors flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t("serviceTokens.createServiceToken")}
            </button>
          </div>

          {/* Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead style={{ borderBottom: '1px solid var(--border)', background: 'var(--gold-pale)' }}>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                      {t("serviceTokens.serviceName")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                      {t("common.description")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                      {t("common.status")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                      {t("serviceTokens.lastUsed")}
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                      {t("common.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredTokens.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                        {t("serviceTokens.noServiceTokens")}
                      </td>
                    </tr>
                  ) : (
                    filteredTokens.map((serviceToken) => (
                      <tr key={serviceToken.id} className="hover:bg-[var(--gold-pale)]">
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">{serviceToken.service_name}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-500">{serviceToken.description || "-"}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              serviceToken.is_active ? "bg-[var(--success-bg)] text-[var(--success)]" : "bg-[var(--error-bg)] text-[var(--error)]"
                            }`}
                          >
                            {serviceToken.is_active ? t("common.active") : t("common.inactive")}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatRelativeTime(serviceToken.last_used_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                          <button
                            onClick={() => handleToggleStatus(serviceToken)}
                            className="text-[var(--navy)] hover:text-[var(--navy-light)]"
                          >
                            {serviceToken.is_active ? t("serviceTokens.deactivate") : t("serviceTokens.activate")}
                          </button>
                          <button
                            onClick={() => openRegenerateModal(serviceToken)}
                            className="text-yellow-600 hover:text-yellow-900"
                          >
                            {t("serviceTokens.regenerate")}
                          </button>
                          <button onClick={() => openDeleteModal(serviceToken)} className="text-red-600 hover:text-red-900">
                            {t("common.delete")}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">{t("serviceTokens.createServiceToken")}</h3>
            <form onSubmit={handleCreate}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("serviceTokens.serviceName")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.service_name}
                    onChange={(e) => setFormData({ ...formData, service_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--gold)] focus:border-[var(--gold)]"
                    placeholder="e.g., billing-service"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t("common.description")}</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--gold)] focus:border-[var(--gold)]"
                    rows={2}
                    placeholder={t("common.optional")}
                  />
                </div>
              </div>

              {formError && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {formError}
                </div>
              )}

              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-[var(--gold-pale)] transition-colors"
                >
                  {t("common.cancel")}
                </button>
                <button
                  type="submit"
                  disabled={formLoading}
                  className="flex-1 px-4 py-2 bg-[var(--navy)] text-white rounded-lg hover:bg-[var(--navy-light)] transition-colors disabled:opacity-50"
                >
                  {formLoading ? t("common.creating") : t("serviceTokens.createServiceToken")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && selectedToken && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">{t("serviceTokens.deleteServiceToken")}</h3>
            <p className="text-gray-600 mb-6">
              {t("serviceTokens.deleteConfirmation", { name: selectedToken.service_name })}
            </p>

            {formError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {formError}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-[var(--gold-pale)] transition-colors"
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={handleDelete}
                disabled={formLoading}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {formLoading ? t("common.deleting") : t("common.delete")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Regenerate Modal */}
      {showRegenerateModal && selectedToken && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">{t("serviceTokens.regenerateToken")}</h3>
            <div className="mb-6">
              <p className="text-gray-600 mb-4">
                {t("serviceTokens.regenerateConfirmation", { name: selectedToken.service_name })}
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  {t("serviceTokens.regenerateWarning")}
                </p>
              </div>
            </div>

            {formError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {formError}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setShowRegenerateModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-[var(--gold-pale)] transition-colors"
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={handleRegenerate}
                disabled={formLoading}
                className="flex-1 px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50"
                style={{ background: '#d97706' }}
              >
                {formLoading ? t("serviceTokens.regenerating") : t("serviceTokens.regenerate")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Secret Key Modal */}
      {showSecretKeyModal && (
        <SecretKeyModal
          secretKey={secretKey}
          keyName={secretKeyName}
          onClose={() => {
            setShowSecretKeyModal(false);
            setSecretKey("");
            setSecretKeyName("");
          }}
        />
      )}
    </AdminLayout>
  );
};
